<script setup lang="ts">
import { ComboboxGroup, ComboboxLabel } from 'reka-ui';
import { computed, useAttrs } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import { comboboxGroupLabelVariants, comboboxGroupVariants } from './combobox-variants';
import { injectComboboxContext } from './context';
import KaComboboxOption from './KaComboboxOption.vue';
import type { KaComboboxGroupProps } from './types';

export type { KaComboboxGroupProps } from './types';

const props = withDefaults(defineProps<KaComboboxGroupProps>(), {
  hideLabel: false,
  options: undefined,
});

const attrs = useAttrs();

const comboboxContext = injectComboboxContext();
const comboboxDataTestid = computed(() => comboboxContext?.dataTestid?.value);
const comboboxId = computed(() => comboboxContext?.id?.value);

// Pass a computed (not a one-time string) so the group's test id tracks runtime changes to the
// parent combobox's dataTestid, keeping the deliberately-reactive injected context intact.
const { dataTestid, suffix } = useDataTestid(
  computed(() => `${comboboxDataTestid.value}-group-${props.label}`),
);

const id = computed(() => {
  if (attrs.id) {
    return attrs.id as string;
  }

  if (comboboxId.value) {
    return `${comboboxId.value}-group-${props.label.replace(/\s+/g, '-').toLowerCase()}`;
  }

  return `combobox-group-${Math.random().toString(36).substr(2, 9)}`;
});

const labelId = computed(() => `${id.value}-label`);
</script>
<template>
  <ComboboxGroup
    :id="id"
    :aria-labelledby="labelId"
    :data-testid="dataTestid"
    :class="cnMerge(comboboxGroupVariants(), attrs.class as VueClass)"
  >
    <ComboboxLabel
      :id="labelId"
      :data-testid="suffix('label')"
      :class="
        cnMerge(comboboxGroupLabelVariants({ hideLabel: props.hideLabel }), props.labelClasses)
      "
    >
      {{ props.label }}
    </ComboboxLabel>

    <slot v-if="!props.options" />

    <KaComboboxOption
      v-for="option in props.options"
      v-else
      v-bind="option"
      :key="`option-${JSON.stringify(option.value)}`"
    />
  </ComboboxGroup>
</template>
